import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { channelsApi } from '@/services/api/channelsApi';
import { extractError } from '@/lib/extractError';
import type { SocialConnection } from '@/types';

interface IntegrationsState {
  connections: SocialConnection[];
  /** Platform name strings returned by the BE (e.g. 'linkedin', 'twitter') */
  platforms: string[];
  isLoadingConnections: boolean;
  isLoadingPlatforms: boolean;
  isConnecting: string | null;
  /** An OAuth connect tab is still open (set by useSocialOAuth). */
  hasOpenConnectTab: boolean;
  /** The platform of the latest open connect tab, when the caller named it. */
  connectTabPlatform: string | null;
  /** Platforms whose connect returned but the backend hasn't persisted yet (refreshUntilConnected). */
  syncingPlatforms: string[];
  error: string | null;
}

interface IntegrationsActions {
  /** Pass `{ silent: true }` to refetch without toggling the loading state (background polls). */
  fetchConnections: (opts?: { silent?: boolean }) => Promise<void>;
  fetchPlatforms: () => Promise<void>;
  connect: (platform: string) => Promise<string | null>;
  disconnect: (platform: string) => Promise<void>;
  clearError: () => void;
}

export const useIntegrationsStore = create<IntegrationsState & IntegrationsActions>()(
  devtools(
    set => ({
      connections: [],
      platforms: [],
      isLoadingConnections: false,
      isLoadingPlatforms: false,
      isConnecting: null,
      hasOpenConnectTab: false,
      connectTabPlatform: null,
      syncingPlatforms: [],
      error: null,

      fetchConnections: async opts => {
        set(
          opts?.silent ? { error: null } : { isLoadingConnections: true, error: null },
          false,
          'integrations/fetchConnections'
        );
        try {
          const raw = await channelsApi.getConnections();
          const connections = Array.isArray(raw) ? raw : [];
          set(
            { connections, isLoadingConnections: false },
            false,
            'integrations/fetchConnections/success'
          );
        } catch (err) {
          // A background refetch (poll, focus, socket reconnect) must not toast:
          // the next one retries, and the page owns its own visible load.
          if (opts?.silent) return;
          set(
            { error: extractError(err), isLoadingConnections: false },
            false,
            'integrations/fetchConnections/error'
          );
        }
      },

      fetchPlatforms: async () => {
        set({ isLoadingPlatforms: true, error: null }, false, 'integrations/fetchPlatforms');
        try {
          const platforms = await channelsApi.getPlatforms();
          set(
            { platforms, isLoadingPlatforms: false },
            false,
            'integrations/fetchPlatforms/success'
          );
        } catch (err) {
          set(
            { error: extractError(err), isLoadingPlatforms: false },
            false,
            'integrations/fetchPlatforms/error'
          );
        }
      },

      connect: async (platform: string) => {
        set({ isConnecting: platform, error: null }, false, 'integrations/connect');
        try {
          const result = await channelsApi.connect(platform);
          set({ isConnecting: null }, false, 'integrations/connect/success');
          return result.authUrl;
        } catch (err) {
          set(
            { error: extractError(err), isConnecting: null },
            false,
            'integrations/connect/error'
          );
          return null;
        }
      },

      disconnect: async (platform: string) => {
        set({ error: null }, false, 'integrations/disconnect');
        try {
          await channelsApi.disconnect(platform);
          set(
            state => ({
              connections: state.connections.filter(
                c => c.platform.toLowerCase() !== platform.toLowerCase()
              ),
            }),
            false,
            'integrations/disconnect/success'
          );
        } catch (err) {
          set({ error: extractError(err) }, false, 'integrations/disconnect/error');
          throw err;
        }
      },

      clearError: () => set({ error: null }, false, 'integrations/clearError'),
    }),
    {
      name: 'IntegrationsStore',
      enabled: import.meta.env.DEV,
    }
  )
);
